import * as v from "valibot";
import { rpcContract } from "../rpc";

const localizedText = v.object({ en: v.optional(v.string()), nl: v.optional(v.string()) });
const localizedTextRequired = v.object({ en: v.string(), nl: v.string() });

const questionnaireStatus = v.picklist(["draft", "published", "archived"]);
const questionType = v.picklist(["single", "multi"]);

const optionInput = v.object({
  id: v.optional(v.string()),
  labelI18n: localizedTextRequired,
});

const questionInput = v.object({
  id: v.optional(v.string()),
  textI18n: localizedTextRequired,
  questionType: questionType,
  options: v.pipe(v.array(optionInput), v.minLength(1)),
});

export const adminListQuestionnairesRpcContract = rpcContract({
  name: "adminListQuestionnaires",
  paramsSchema: v.object({
    limit: v.pipe(v.optional(v.number(), 25), v.minValue(1), v.maxValue(100)),
    offset: v.pipe(v.optional(v.number(), 0), v.minValue(0)),
    search: v.optional(v.string()),
    status: v.optional(questionnaireStatus),
    influencerId: v.optional(v.string()),
    sortBy: v.optional(v.picklist(["createdAt", "updatedAt", "title"])),
    sortOrder: v.optional(v.picklist(["asc", "desc"])),
  }),
  resultSchema: v.object({
    questionnaires: v.array(
      v.object({
        id: v.string(),
        influencerId: v.string(),
        influencerHandle: v.string(),
        titleI18n: localizedText,
        status: questionnaireStatus,
        questionCount: v.number(),
        communityCount: v.number(),
        responseCount: v.number(),
        createdAt: v.string(),
        updatedAt: v.string(),
      }),
    ),
    totalCount: v.number(),
  }),
});

export const adminGetQuestionnaireRpcContract = rpcContract({
  name: "adminGetQuestionnaire",
  paramsSchema: v.object({ id: v.string() }),
  resultSchema: v.object({
    id: v.string(),
    influencerId: v.string(),
    influencerHandle: v.string(),
    titleI18n: localizedText,
    introI18n: localizedText,
    status: questionnaireStatus,
    questions: v.array(
      v.object({
        id: v.string(),
        textI18n: localizedText,
        questionType: questionType,
        position: v.number(),
        options: v.array(
          v.object({
            id: v.string(),
            labelI18n: localizedText,
            position: v.number(),
            hasResponses: v.boolean(),
          }),
        ),
      }),
    ),
    linkedCommunityIds: v.array(v.string()),
  }),
});

export const adminSaveQuestionnaireRpcContract = rpcContract({
  name: "adminSaveQuestionnaire",
  paramsSchema: v.object({
    id: v.optional(v.string()),
    influencerId: v.string(),
    titleI18n: localizedTextRequired,
    introI18n: localizedTextRequired,
    status: questionnaireStatus,
    questions: v.array(questionInput),
    linkedCommunityIds: v.array(v.string()),
  }),
  resultSchema: v.object({ id: v.string() }),
});

export const adminDeleteQuestionnaireRpcContract = rpcContract({
  name: "adminDeleteQuestionnaire",
  paramsSchema: v.object({ id: v.string() }),
  resultSchema: v.object({ success: v.boolean() }),
});

export const adminListCreatorCommunitiesRpcContract = rpcContract({
  name: "adminListCreatorCommunities",
  paramsSchema: v.object({ influencerId: v.string() }),
  resultSchema: v.object({
    communities: v.array(v.object({ id: v.string(), name: v.string(), status: v.string() })),
  }),
});

export const adminFindCreatorByHandleRpcContract = rpcContract({
  name: "adminFindCreatorByHandle",
  paramsSchema: v.object({ handle: v.pipe(v.string(), v.minLength(1)) }),
  resultSchema: v.object({
    creator: v.nullable(v.object({ id: v.string(), handle: v.string() })),
  }),
});

export type AdminListQuestionnairesParams = v.InferOutput<typeof adminListQuestionnairesRpcContract.paramsSchema>;
export type AdminListQuestionnairesResult = v.InferOutput<typeof adminListQuestionnairesRpcContract.resultSchema>;
export type AdminQuestionnaireListItem = AdminListQuestionnairesResult["questionnaires"][number];
export type AdminGetQuestionnaireParams = v.InferOutput<typeof adminGetQuestionnaireRpcContract.paramsSchema>;
export type AdminGetQuestionnaireResult = v.InferOutput<typeof adminGetQuestionnaireRpcContract.resultSchema>;
export type AdminSaveQuestionnaireParams = v.InferOutput<typeof adminSaveQuestionnaireRpcContract.paramsSchema>;
export type AdminSaveQuestionnaireResult = v.InferOutput<typeof adminSaveQuestionnaireRpcContract.resultSchema>;
export type AdminDeleteQuestionnaireParams = v.InferOutput<typeof adminDeleteQuestionnaireRpcContract.paramsSchema>;
export type AdminListCreatorCommunitiesParams = v.InferOutput<
  typeof adminListCreatorCommunitiesRpcContract.paramsSchema
>;
export type AdminListCreatorCommunitiesResult = v.InferOutput<
  typeof adminListCreatorCommunitiesRpcContract.resultSchema
>;
export type AdminFindCreatorByHandleParams = v.InferOutput<typeof adminFindCreatorByHandleRpcContract.paramsSchema>;
export type AdminFindCreatorByHandleResult = v.InferOutput<typeof adminFindCreatorByHandleRpcContract.resultSchema>;
